<script setup>
import { ref } from 'vue';

const emit = defineEmits(['add-field']);

const groups = [
 {
 label: 'Basico',
 fields: [
 { type: 'text', label: 'Texto', icon: 'i-lucide-type' },
 { type: 'email', label: 'Email', icon: 'i-lucide-mail' },
 { type: 'phone', label: 'Telefone', icon: 'i-lucide-phone' },
 { type: 'number', label: 'Numero', icon: 'i-lucide-hash' },
 { type: 'date', label: 'Data', icon: 'i-lucide-calendar' },
 ],
 },
 {
 label: 'Escolha',
 fields: [
 {
 type: 'select',
 label: 'Selecionar',
 icon: 'i-lucide-chevron-down',
 },
 {
 type: 'multi_select',
 label: 'Selecao Multipla',
 icon: 'i-lucide-list-checks',
 },
 {
 type: 'radio',
 label: 'Radio',
 icon: 'i-lucide-circle-dot',
 },
 {
 type: 'checkbox',
 label: 'Checkbox',
 icon: 'i-lucide-check-square',
 },
 ],
 },
 {
 label: 'Conteúdo',
 fields: [
 {
 type: 'textarea',
 label: 'Texto Longo',
 icon: 'i-lucide-align-left',
 },
 {
 type: 'file',
 label: 'Upload',
 icon: 'i-lucide-upload',
 },
 ],
 },
 {
 label: 'Layout',
 fields: [
 {
 type: 'heading',
 label: 'Título',
 icon: 'i-lucide-heading',
 },
 {
 type: 'paragraph',
 label: 'Paragrafo',
 icon: 'i-lucide-pilcrow',
 },
 {
 type: 'divider',
 label: 'Divisor',
 icon: 'i-lucide-minus',
 },
 ],
 },
 {
 label: 'Dados Brasil',
 fields: [
 { type: 'cpf', label: 'CPF', icon: 'i-lucide-fingerprint' },
 { type: 'cnpj', label: 'CNPJ', icon: 'i-lucide-building-2' },
 { type: 'cep', label: 'CEP', icon: 'i-lucide-map-pin' },
 { type: 'currency', label: 'Moeda (R$)', icon: 'i-lucide-coins' },
 ],
 },
 {
 label: 'Avançado',
 fields: [
 { type: 'rating', label: 'Avaliação', icon: 'i-lucide-star' },
 {
 type: 'scale',
 label: 'Escala',
 icon: 'i-lucide-gauge',
 },
 {
 type: 'hidden',
 label: 'Oculto',
 icon: 'i-lucide-eye-off',
 },
 ],
 },
];

const collapsedGroups = ref(new Set());

function toggleGroup(label) {
 if (collapsedGroups.value.has(label)) {
 collapsedGroups.value.delete(label);
 } else {
 collapsedGroups.value.add(label);
 }
}

function onDragStart(event, fieldType) {
 event.dataTransfer.setData('field-type', fieldType);
 event.dataTransfer.effectAllowed = 'copy';
}

function onAddField(fieldType) {
 emit('add-field', fieldType);
}
</script>

<template>
 <div class="py-3">
 <div class="px-4 mb-3">
 <h3
 class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider"
 >
 Campos
 </h3>
 </div>

 <div
 v-for="group in groups"
 :key="group.label"
 class="mb-1"
 >
 <!-- Group header -->
 <button
 class="flex items-center justify-between w-full px-4 py-1.5 text-xs font-medium text-n-slate-10 hover:text-n-slate-12 transition-colors"
 @click="toggleGroup(group.label)"
 >
 {{ group.label }}
 <span
 class="i-lucide-chevron-down size-3 transition-transform duration-200"
 :class="
 collapsedGroups.has(group.label) ? '-rotate-90' : ''
 "
 />
 </button>

 <!-- Group fields -->
 <Transition
 enter-active-class="transition-all duration-200"
 leave-active-class="transition-all duration-150"
 enter-from-class="opacity-0 max-h-0"
 enter-to-class="opacity-100 max-h-96"
 leave-from-class="opacity-100 max-h-96"
 leave-to-class="opacity-0 max-h-0"
 >
 <div
 v-if="!collapsedGroups.has(group.label)"
 class="px-3 pb-2 space-y-0.5 overflow-hidden"
 >
 <div
 v-for="field in group.fields"
 :key="field.type"
 draggable="true"
 class="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-1/50 cursor-grab active:cursor-grabbing transition-colors select-none"
 @dragstart="onDragStart($event, field.type)"
 @click="onAddField(field.type)"
 >
 <div
 class="flex items-center justify-center size-7 rounded-md bg-n-slate-3 shrink-0"
 >
 <span
 :class="field.icon"
 class="size-3.5 text-n-slate-10"
 />
 </div>
 <span class="text-xs font-medium">{{ field.label }}</span>
 <span
 class="i-lucide-grip-vertical size-3 text-n-slate-7 ml-auto"
 />
 </div>
 </div>
 </Transition>
 </div>

 <!-- Tracking fields bundle -->
 <div class="px-3 pb-3 mt-2 border-t border-n-weak pt-3">
 <button
 class="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/15 transition-colors border border-emerald-200 dark:border-emerald-800"
 @click="onAddField('tracking_bundle')"
 >
 <div class="flex items-center justify-center size-7 rounded-md bg-emerald-100 dark:bg-emerald-500/20 shrink-0">
 <span class="i-lucide-radar size-3.5 text-emerald-600 dark:text-emerald-400" />
 </div>
 <div class="text-left">
 <span class="text-xs font-semibold block">Campos de Tracking</span>
 <span class="text-[10px] text-emerald-600/70 dark:text-emerald-400/70">UTMs, referrer, page URL</span>
 </div>
 </button>
 </div>
 </div>
</template>
